import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useDomainConfig } from '../../hooks/useDomainConfig';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  platform?: string;
  type?: 'website' | 'article' | 'video.other';
  image?: string;
  noindex?: boolean;
  jsonLd?: object[];
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  platform,
  type = 'website',
  image,
  noindex = false,
  jsonLd = []
}) => {
  const domainConfig = useDomainConfig();
  const location = useLocation();

  if (!domainConfig) return null;

  const baseUrl = `https://${domainConfig.domain}`;

  // Dynamic title generation
  const pageTitle = title || (platform
    ? `${platform} Video Downloader - Free, No Watermark | ${domainConfig.branding.name}`
    : `Free Video Downloader - 50+ Platforms | ${domainConfig.branding.name}`);

  // Dynamic description
  const pageDescription = description || (platform
    ? `Download ${platform} videos in HD/4K quality. Free, fast, no registration. Support MP4, MP3, WebM formats. Best ${platform} downloader 2025.`
    : `Download videos from YouTube, TikTok, Instagram, Twitter/X and 50+ platforms. Free online video downloader with no ads or registration.`);

  const pageImage = image || `${baseUrl}/og-image.png`;

  // Base structured data
  const baseJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: domainConfig.branding.name,
    url: baseUrl,
    description: domainConfig.seo.description,
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
  };

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={`${baseUrl}${location.pathname}`} />

      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:url" content={`${baseUrl}${location.pathname}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={domainConfig.branding.name} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={`${baseUrl}${location.pathname}`} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      <script type="application/ld+json">
        {JSON.stringify(baseJsonLd)}
      </script>
      {jsonLd.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};
